import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react'
import { Mail, Zap, Shield, ArrowRight, Briefcase, Users, DollarSign, Building, Calendar, TrendingUp, ArrowUp, Check, Play, Sun, Calculator, Menu, X } from 'lucide-react'
import './App.css';
import Cal, { getCalApi } from "@calcom/embed-react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// Navigation component
function Navigation({ isOpen, setIsOpen }) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);

  // Add smooth scroll handler
  const handleNavClick = (e, targetId) => {
    e.preventDefault();
    const element = targetId === 'top' ? document.body : document.getElementById(targetId);
    if (element) {
      const headerOffset = 100; // Adjust this value based on your header height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });

      // Close mobile menu if open
      if (isOpen) setIsOpen(false);
    }
  };

  return (
    <>
      {/* Mobile menu button */}
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className={`
          md:hidden fixed top-4 right-4 z-50 bg-orange-600 text-white p-2 rounded-full shadow-lg
          transition-transform duration-300 ${scrolled ? 'transform translate-y-1' : ''}
        `}
      >
        {isOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Navigation menu - mobile first */}
      <nav className={`
        fixed top-0 left-0 right-0 z-40
        transition-all duration-300 ease-in-out
        ${scrolled ? 'bg-[#f7f5ed]/95 backdrop-blur-sm shadow-md' : 'bg-transparent'}
        ${isOpen ? 'h-full md:h-auto' : 'h-auto'}
      `}>
        <div className={`
          container mx-auto
          flex flex-col md:flex-row md:justify-between items-center
          gap-6 p-4 md:p-2
          ${isOpen ? 'mt-12 md:mt-0' : 'mt-0'}
          relative
        `}>
          {/* Logo section */}
          <a 
            href="#top" 
            onClick={(e) => handleNavClick(e, 'top')}
            className={`
              flex items-center gap-2 hover:opacity-80 transition-opacity
              md:justify-start
              ${isOpen ? 'w-full justify-center' : 'justify-start'}
            `}
          >
            <img 
              src="/Whiteboard e (1).png" 
              alt="Email Protocol Logo" 
              className={`
                object-contain
                ${isOpen ? 'hidden md:block md:w-12 md:h-12' : 'w-10 h-10 sm:w-12 sm:h-12'}
              `}
            />
            <span className={`
              font-bold
              ${isOpen ? 'text-xl text-center' : 'text-base'}
              md:text-base
            `}>Email Protocol</span>
          </a>

          {/* Mobile menu background */}
          {isOpen && (
            <div className="fixed inset-0 bg-[#f7f5ed] z-[-1] md:hidden" />
          )}

          {/* Navigation links */}
          <div className={`
            flex flex-col md:flex-row items-center gap-8
            ${isOpen ? 'flex bg-[#f7f5ed] w-full h-[calc(100vh-80px)] md:h-auto md:w-auto md:bg-transparent' : 'hidden md:flex'}
            transition-all duration-300 ease-in-out
            ${isOpen ? 'opacity-100' : 'opacity-0 md:opacity-100'}
            ${isOpen ? 'py-4' : 'py-0'}
          `}>
            {/* Mobile Icon Menu */}
            {isOpen && (
              <div className="w-full flex flex-col items-center justify-center mt-8 md:hidden">
                <div className="grid grid-cols-2 gap-x-8 gap-y-8">
                  <button
                    onClick={(e) => handleNavClick(e, 'challenges')}
                    className="flex flex-col items-center justify-center w-24 h-24 bg-white rounded-xl shadow hover:bg-orange-100 transition"
                  >
                    <Zap className="w-8 h-8 text-orange-600 mb-1" />
                    <span className="text-sm font-mono font-bold text-black">Challenges</span>
                  </button>
                  <button
                    onClick={(e) => handleNavClick(e, 'case-studies')}
                    className="flex flex-col items-center justify-center w-24 h-24 bg-white rounded-xl shadow hover:bg-orange-100 transition"
                  >
                    <TrendingUp className="w-8 h-8 text-orange-600 mb-1" />
                    <span className="text-sm font-mono font-bold text-black">Results</span>
                  </button>
                  <button
                    onClick={(e) => handleNavClick(e, 'pricing')}
                    className="flex flex-col items-center justify-center w-24 h-24 bg-white rounded-xl shadow hover:bg-orange-100 transition"
                  >
                    <DollarSign className="w-8 h-8 text-orange-600 mb-1" />
                    <span className="text-sm font-mono font-bold text-black">Pricing</span>
                  </button>
                  <button
                    onClick={(e) => handleNavClick(e, 'why-email')}
                    className="flex flex-col items-center justify-center w-24 h-24 bg-white rounded-xl shadow hover:bg-orange-100 transition"
                  >
                    <Mail className="w-8 h-8 text-orange-600 mb-1" />
                    <span className="text-sm font-mono font-bold text-black">Why Email</span>
                  </button>
                  <button
                    onClick={(e) => handleNavClick(e, 'contact')}
                    className="flex flex-col items-center justify-center w-24 h-24 bg-orange-600 rounded-xl shadow hover:bg-orange-500 transition col-span-2"
                  >
                    <Calendar className="w-8 h-8 text-white mb-1" />
                    <span className="text-sm font-mono font-bold text-white">Talk To Us</span>
                  </button>
                </div>
              </div>
            )}
            {/* Desktop Text Menu */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#challenges" onClick={(e) => handleNavClick(e, 'challenges')} className="text-black hover:text-orange-600 font-semibold transition-colors text-base">Challenges</a>
              <a href="#case-studies" onClick={(e) => handleNavClick(e, 'case-studies')} className="text-black hover:text-orange-600 font-semibold transition-colors text-base">Results</a>
              <a href="#pricing" onClick={(e) => handleNavClick(e, 'pricing')} className="text-black hover:text-orange-600 font-semibold transition-colors text-base">Pricing</a>
              <a href="#why-email" onClick={(e) => handleNavClick(e, 'why-email')} className="text-black hover:text-orange-600 font-semibold transition-colors text-base">Why Email</a>
              <a href="#contact" onClick={(e) => handleNavClick(e, 'contact')} className="bg-orange-600 text-white px-6 py-3 rounded-full hover:bg-orange-500 transition-colors text-base">Talk To Us</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Overlay for mobile */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
}

const testimonials = [
  {
    id: 1,
    company: "Michael Rose & Associates",
    quote: "The Email Protocol helped us increase our monthly email send volume by 400% and boost our closed deals by 300%.",
    caseStudyLink: "https://email-protocol-case-stud-j8veye9.gamma.site/",
    backgroundImage: "/images/testimonial-bg1.jpg",
    icon: DollarSign
  },
  {
    id: 2,
    company: "Sunray Energy Solutions",
    quote: "The Email Protocol helped us achieve a 150% increase in qualified leads and double our lead-to-customer conversion rate.",
    caseStudyLink: "https://sunraycasestudy.vercel.app/case-study",
    backgroundImage: "/images/testimonial-bg2.jpg",
    icon: Sun
  },
  {
    id: 3,
    company: "Humble Co",
    quote: "From zero to five clients in just five months using cold email, with over 500 meetings booked. The Email Protocol's system delivered exactly what we needed.",
    caseStudyLink: "https://v0-cold-email-case-study.vercel.app/",
    backgroundImage: "/images/testimonial-bg3.jpg",
    icon: TrendingUp
  }
]

const whyUsData = [
  { 
    icon: Mail, 
    title: "Cold Email Specialists", 
    content: "100% focus on B2B cold email\nWe exclusively do cold email and related tasks\nTailored strategies for established businesses"
  },
  { 
    icon: Users, 
    title: "Selective Partnerships", 
    content: "We work exclusively with:\nB2B companies with proven case studies\nBusinesses that have achieved product-market fit"
  },
  { 
    icon: Briefcase, 
    title: "Proven Expertise", 
    content: "Deep understanding of business dynamics\nContinuous study of email technology advancements\nApplying psychological principles to optimize outreach"
  },
  { 
    icon: Zap, 
    title: "Accelerated Growth Timeline", 
    content: "Day 1: Comprehensive onboarding\nBy Day 3: Complete system setup\nDays 8-21: Start generating meetings"
  },
  {
    icon: Building,
    title: "About Us",
    content: "Dedicated team of cold email specialists\nFocused on helping B2B companies achieve sales goals\nEffective outreach strategies"
  },
  {
    icon: Calendar,
    title: "What We Do",
    content: "Craft personalized cold email campaigns\nOptimize outreach strategies\nProvide ongoing support to ensure success"
  },
  {
    icon: TrendingUp,
    title: "How We Do It",
    content: "Utilize advanced targeting techniques\nLeverage data-driven insights\nCreate campaigns that resonate with your target audience"
  },
  {
    icon: ArrowUp,
    title: "Our Process",
    content: "Understand your business needs\nDevelop tailored strategy\nExecute campaign\nAnalyze results for continuous improvement"
  },
  {
    icon: DollarSign,
    title: "ROI-Driven Pricing Model",
    content: "Performance-based pricing: We perform or you don't pay\nOur success is tied to your success\nThe more money you make, the more we make\nWe believe in our capabilities and put our money where our mouth is"
  }
];

const problemsData = [
  { title: "Struggling with Lead Generation", description: "Unable to consistently find and engage with qualified prospects" },
  { title: "Insufficient Sales Pipeline", description: "Sales team lacks enough opportunities to meet revenue targets" },
  { title: "Low Meeting Volume", description: "Not booking enough meetings with potential clients" },
  { title: "Poor Lead Quality", description: "Wasting time on unqualified leads that don't convert" },
  { title: "Stagnant Revenue Growth", description: "Unable to scale sales and revenue despite market potential" },
  { title: "Ineffective Outreach Strategies", description: "Current email campaigns fail to resonate with target audience" }
];

export function LandingPage() {
  const [glitchText, setGlitchText] = useState('The Email Protocol')
  const [activeHowItWorks, setActiveHowItWorks] = useState(0)
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false)
  const [closeRate, setCloseRate] = useState(20)
  const [annualCustomerValue, setAnnualCustomerValue] = useState(10000)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [activeAccordion, setActiveAccordion] = useState('')

  const roiCalculations = useMemo(() => {
    const monthlyEmails = 10000;
    const monthlyLeads = 5000;
    const averageReplyRate = 0.03; // 3%
    const positiveReplyRate = 0.15; // 15%
    const meetingBookedRate = 0.50; // 50%
    const monthlyCost = 877;

    const monthlyReplies = monthlyEmails * averageReplyRate;
    const monthlyPositiveReplies = monthlyReplies * positiveReplyRate;
    const monthlyMeetings = monthlyPositiveReplies * meetingBookedRate;
    const monthlyDeals = monthlyMeetings * (closeRate / 100);
    const monthlyRevenue = monthlyDeals * (annualCustomerValue);
    const monthlyROI = (monthlyRevenue / monthlyCost);

    return {
      monthlyEmails,
      monthlyLeads,
      monthlyReplies: Math.round(monthlyReplies),
      monthlyPositiveReplies: Math.round(monthlyPositiveReplies),
      monthlyMeetings: Math.round(monthlyPositiveReplies * meetingBookedRate),
      monthlyDeals: monthlyDeals.toFixed(1),
      monthlyRevenue: monthlyRevenue.toFixed(2),
      monthlyROI: Math.round(monthlyROI),
      monthlyCost
    };
  }, [closeRate, annualCustomerValue]);

  useEffect(() => {
    const glitchInterval = setInterval(() => {
      const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
      let result = ''
      for (let i = 0; i < 18; i++) {
        result += characters.charAt(Math.floor(Math.random() * characters.length))
      }
      setGlitchText(result)
      setTimeout(() => setGlitchText('The Email Protocol'), 100)
    }, 3000)

    return () => clearInterval(glitchInterval)
  }, [])

  useEffect(() => {
    (async function () {
      const cal = await getCalApi({ "namespace": "30min-meeting" });
      cal("ui", {
        "styles": { 
          "branding": { "brandColor": "#fc5900" },
          "theme": "light"
        },
        "hideEventTypeDetails": false,
        "layout": "column_view"
      });
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#f7f5ed] text-black font-mono relative">
      <Navigation isOpen={isMenuOpen} setIsOpen={setIsMenuOpen} />
      <main className="container mx-auto px-4 pt-24 sm:pt-28 pb-8 sm:pb-12">
        {/* Hero section */}
        <section className="text-center mb-12 sm:mb-20">
          <div className="bg-yellow-300/90 text-black py-2 px-4 mb-6 sm:mb-8 inline-block rounded-full text-base sm:text-lg font-extrabold font-sans tracking-wider transform -rotate-2 vibrate relative before:absolute before:inset-0 before:-z-10 before:rounded-full before:p-[2px] before:bg-gradient-to-r before:from-purple-400/90 before:via-pink-400/90 before:to-purple-400/90 before:animate-border-shine before:blur-[2px]">
            ATTENTION B2B BUSINESSES: COLD EMAIL BROUGHT YOU HERE
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold mb-4 sm:mb-6 relative">
            <div className="flex items-center justify-center gap-8 mb-6">
              <div className="relative">
                <span className="text-black">{glitchText}</span>
                <span className="absolute top-0 left-0 right-0 text-orange-600 opacity-50 filter blur-sm">
                  {glitchText}
                </span>
              </div>
            </div>
          </h1>
          <p className="text-lg sm:text-xl mb-6 sm:mb-8">
            We get real responses from your ideal clients and book meetings with your target market.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button 
              onClick={() => setIsVideoModalOpen(true)}
              className="inline-flex items-center bg-white text-black px-4 sm:px-6 py-2 sm:py-3 rounded-full text-base sm:text-lg font-semibold hover:bg-gray-100 transition-all shadow-md border border-gray-300 animate-pulse-glow-scale"
            >
              <Play className="w-5 h-5 sm:w-6 sm:h-6 mr-2 animate-bounce" /> Play Short Video
            </button>
            <a
              href="#contact"
              className="inline-flex items-center bg-orange-600 text-white px-6 py-3 rounded-full text-base sm:text-lg font-semibold hover:bg-orange-500 transition-colors shadow-md border border-orange-600"
            >
              Talk To Us
            </a>
          </div>
        </section>

        {/* Common Challenges section */}
        <section id="challenges" className="mb-12 sm:mb-20">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 text-center">Common Challenges We Solve</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {problemsData.map((problem, index) => (
              <div 
                key={index}
                className="bg-[#e8e4d8] p-4 sm:p-6 rounded-lg text-center hover:bg-[#d8d4c8] transition-colors cursor-pointer relative overflow-hidden double-border"
                style={{
                  boxShadow: '4px 4px 0 0 #000',
                  border: '3px solid #ff0000',
                  transform: 'translate(-4px, -4px)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translate(-2px, -2px)';
                  e.currentTarget.style.boxShadow = '2px 2px 0 0 #000';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translate(-4px, -4px)';
                  e.currentTarget.style.boxShadow = '4px 4px 0 0 #000';
                }}
              >
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-orange-700">{problem.title}</h3>
                <p className="text-sm sm:text-base text-gray-700">{problem.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-12 sm:mb-20">
          <h2 className="text-4xl font-bold mb-8 text-center text-black">AI-Powered Cold Email Outreach</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* AI-Powered Precision card */}
            <div 
              className="bg-[#e8e4d8] p-6 rounded-lg relative overflow-hidden double-border cursor-pointer"
              style={{
                boxShadow: '4px 4px 0 0 #000',
                border: '3px solid #000',
                transform: 'translate(-4px, -4px)',
                transition: 'transform 0.2s, box-shadow 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translate(-2px, -2px)';
                e.currentTarget.style.boxShadow = '2px 2px 0 0 #000';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translate(-4px, -4px)';
                e.currentTarget.style.boxShadow = '4px 4px 0 0 #000';
              }}
            >
              <h3 className="text-2xl font-semibold mb-4 text-orange-600 text-center">Done-For-You Cold Email</h3>
              <div className="text-center mb-6">
                <Zap className="w-16 h-16 mx-auto text-orange-600 mb-4" />
              </div>
              <ul className="list-none space-y-4">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0 mt-1" />
                  <span>Full backend infrastructure setup</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0 mt-1" />
                  <span>10,000 targeted emails monthly</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0 mt-1" />
                  <span>Custom-written email sequences</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0 mt-1" />
                  <span>Advanced targeting & list building</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0 mt-1" />
                  <span>Ongoing campaign optimization</span>
                </li>
              </ul>
            </div>

            {/* Guaranteed Results card */}
            <div 
              className="bg-[#e8e4d8] p-6 rounded-lg relative overflow-hidden double-border cursor-pointer"
              style={{
                boxShadow: '4px 4px 0 0 #000',
                border: '3px solid #000',
                transform: 'translate(-4px, -4px)',
                transition: 'transform 0.2s, box-shadow 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translate(-2px, -2px)';
                e.currentTarget.style.boxShadow = '2px 2px 0 0 #000';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translate(-4px, -4px)';
                e.currentTarget.style.boxShadow = '4px 4px 0 0 #000';
              }}
            >
              <h3 className="text-2xl font-semibold mb-4 text-orange-600 text-center">Predictable Results</h3>
              <div className="text-center mb-6">
                <TrendingUp className="w-16 h-16 mx-auto text-orange-600 mb-4" />
              </div>
              <ul className="list-none space-y-4">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0 mt-1" />
                  <span>10-30 qualified meetings monthly</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0 mt-1" />
                  <span>Weekly performance reporting</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0 mt-1" />
                  <span>Response handling training</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0 mt-1" />
                  <span>No long-term contracts</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0 mt-1" />
                  <span>Flat $877/month, no hidden fees</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Case Studies section */}
        <section id="case-studies" className="mb-20 sm:mb-28 pb-8">
          <h2 className="text-4xl font-bold mb-12 text-center text-black">Success Stories & Case Studies</h2>
          
          {/* Desktop view - Convert to carousel */}
          <div className="hidden md:block">
            <Slider
              dots={true}
              infinite={true}
              speed={300}
              slidesToShow={2}
              slidesToScroll={1}
              arrows={false}
              autoplay={true}
              autoplaySpeed={3000}
              className="case-studies-slider"
              responsive={[
                {
                  breakpoint: 1024,
                  settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1
                  }
                }
              ]}
            >
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="px-4">
                  <div
                    className="group bg-gradient-to-br from-orange-100 to-yellow-100 p-6 rounded-2xl shadow-lg transition-all duration-300 h-[400px] flex flex-col cursor-pointer relative overflow-hidden"
                    style={{
                      boxShadow: '-4px 4px 0 0 #000',
                      border: '3px solid #000',
                      transform: 'translate(4px, -4px)',
                    }}
                    onClick={() => window.open(testimonial.caseStudyLink, '_blank', 'noopener,noreferrer')}
                  >
                    <div 
                      className="absolute inset-0 bg-white rounded-2xl transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1" 
                      style={{ transform: 'translate(-8px, 8px)', zIndex: -1, border: '3px solid #000' }}
                    />
                    <div className="absolute top-0 right-0 w-24 h-24 bg-orange-400 rounded-bl-full z-0 opacity-50" />
                    <div className="flex flex-col items-center relative z-10 h-full">
                      <testimonial.icon className="w-20 h-20 text-orange-600 mb-4" />
                      <h3 className="text-xl font-semibold text-orange-600 mb-6">{testimonial.company}</h3>
                      <p className="text-gray-900 text-center mb-6 italic flex-grow">
                        "<span className="text-orange-800 font-semibold">{testimonial.quote}</span>"
                      </p>
                      <span className="mt-auto inline-block bg-orange-500 text-white px-6 py-2 rounded-full font-bold group-hover:bg-black group-hover:text-white border-2 border-black transition-all duration-300 text-sm shadow-md">
                        Read Case Study
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </Slider>
          </div>

          {/* Mobile carousel - already using Slider */}
          <div className="md:hidden">
            <Slider
              dots={true}
              infinite={true}
              speed={300}
              slidesToShow={1}
              slidesToScroll={1}
              arrows={false}
              autoplay={true}
              autoplaySpeed={3000}
              className="case-studies-slider"
            >
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="px-4">
                  <div
                    className="group bg-gradient-to-br from-orange-100 to-yellow-100 p-6 rounded-2xl shadow-lg transition-all duration-300 h-[400px] flex flex-col cursor-pointer relative overflow-hidden"
                    style={{
                      boxShadow: '-4px 4px 0 0 #000',
                      border: '3px solid #000',
                      transform: 'translate(4px, -4px)',
                    }}
                    onClick={() => window.open(testimonial.caseStudyLink, '_blank', 'noopener,noreferrer')}
                  >
                    <div 
                      className="absolute inset-0 bg-white rounded-2xl" 
                      style={{ transform: 'translate(-8px, 8px)', zIndex: -1, border: '3px solid #000' }}
                    />
                    <div className="absolute top-0 right-0 w-24 h-24 bg-orange-400 rounded-bl-full z-0 opacity-50" />
                    <div className="flex flex-col items-center relative z-10 h-full">
                      <testimonial.icon className="w-20 h-20 text-orange-600 mb-4" />
                      <h3 className="text-xl font-semibold text-orange-600 mb-6">{testimonial.company}</h3>
                      <p className="text-gray-900 text-center mb-6 italic flex-grow">
                        "<span className="text-orange-800 font-semibold">{testimonial.quote}</span>"
                      </p>
                      <span className="mt-auto inline-block bg-orange-500 text-white px-6 py-2 rounded-full font-bold border-2 border-black transition-all duration-300 text-sm shadow-md">
                        Read Case Study
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="mb-12 sm:mb-20">
          <h2 className="text-4xl font-bold mb-4 text-center text-black">Choose Your Growth Path</h2>
          <p className="text-xl text-center text-gray-700 mb-16 italic max-w-3xl mx-auto">
            From startup validation to enterprise scale, we have the perfect outbound system for your business stage and growth goals.
          </p>

          <div className="max-w-4xl mx-auto space-y-6 mb-16">
            {/* Start Ups Accordion */}
            <div 
              className="bg-[#e8e4d8] rounded-lg relative overflow-hidden cursor-pointer transition-all duration-300"
              style={{
                boxShadow: '4px 4px 0 0 #000',
                border: '3px solid #000',
                transform: 'translate(-4px, -4px)',
              }}
              onClick={() => setActiveAccordion(activeAccordion === 'startups' ? '' : 'startups')}
            >
              <div className="p-6 sm:p-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-center flex-1">
                    <Zap className="w-8 h-8 text-orange-600 mr-4 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="text-xl sm:text-2xl font-bold text-black">Start Ups</h3>
                      <p className="text-gray-700">Perfect for Testing & Growth</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end justify-center ml-4">
                    <div className="text-2xl sm:text-3xl font-bold text-orange-600 mb-1">
                      $877<span className="text-lg font-normal text-gray-600">/month</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <span className="text-sm mr-2">Details</span>
                      <ArrowRight className={`w-4 h-4 transition-transform duration-300 ${activeAccordion === 'startups' ? 'rotate-90' : ''}`} />
                    </div>
                  </div>
                </div>
                
                {activeAccordion === 'startups' && (
                  <div className="mt-8 pt-8 border-t border-gray-400 animate-fade-in">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                        <h4 className="font-bold text-lg mb-4 text-orange-600">What's Included:</h4>
                        <ul className="space-y-3">
                          <li className="flex items-start">
                            <Check className="w-4 h-4 text-orange-600 mr-3 flex-shrink-0 mt-1" />
                            <span className="text-gray-800"><strong>10,000 targeted emails</strong> monthly outbound</span>
                          </li>
                          <li className="flex items-start">
                            <Check className="w-4 h-4 text-orange-600 mr-3 flex-shrink-0 mt-1" />
                            <span className="text-gray-800">Self-serve with <strong>AI inbox management</strong> guidelines</span>
                          </li>
                          <li className="flex items-start">
                            <Check className="w-4 h-4 text-orange-600 mr-3 flex-shrink-0 mt-1" />
                            <span className="text-gray-800">Leads delivered to <strong>Slack</strong> for easy management</span>
                          </li>
                          <li className="flex items-start">
                            <Check className="w-4 h-4 text-orange-600 mr-3 flex-shrink-0 mt-1" />
                            <span className="text-gray-800"><strong>Lead generation</strong> and <strong>copy assistance</strong></span>
                          </li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-bold text-lg mb-4 text-orange-600">Perfect For:</h4>
                        <ul className="space-y-3">
                          <li className="flex items-start">
                            <Check className="w-4 h-4 text-orange-600 mr-3 flex-shrink-0 mt-1" />
                            <span className="text-gray-800">You handle follow-ups and replies</span>
                          </li>
                          <li className="flex items-start">
                            <Check className="w-4 h-4 text-orange-600 mr-3 flex-shrink-0 mt-1" />
                            <span className="text-gray-800">Great for <strong>risk-averse teams</strong> testing outbound</span>
                          </li>
                          <li className="flex items-start">
                            <Check className="w-4 h-4 text-orange-600 mr-3 flex-shrink-0 mt-1" />
                            <span className="text-gray-800">Teams wanting to validate outbound strategies</span>
                          </li>
                        </ul>
                        <a 
                          href="#contact" 
                          className="inline-block bg-orange-600 text-white px-6 py-3 rounded-full font-bold hover:bg-orange-500 transition-colors text-center mt-6"
                        >
                          Start Testing Outbound
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Scaling GTM Strategy Accordion */}
            <div 
              className="bg-[#e8e4d8] rounded-lg relative overflow-hidden cursor-pointer transition-all duration-300"
              style={{
                boxShadow: '4px 4px 0 0 #000',
                border: '3px solid #ff0000',
                transform: 'translate(-4px, -4px)',
              }}
              onClick={() => setActiveAccordion(activeAccordion === 'scaling' ? '' : 'scaling')}
            >
              <div className="absolute top-3 right-3 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold z-10">
                MOST POPULAR
              </div>
              
              <div className="p-6 sm:p-8 pt-12 sm:pt-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-center flex-1">
                    <TrendingUp className="w-8 h-8 text-orange-600 mr-4 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="text-xl sm:text-2xl font-bold text-black">Scaling GTM Strategy</h3>
                      <p className="text-gray-700">Fully Managed Growth Engine</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end justify-center ml-4">
                    <div className="text-2xl sm:text-3xl font-bold text-orange-600 mb-1">
                      $3,877<span className="text-lg font-normal text-gray-600">/month</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <span className="text-sm mr-2">Details</span>
                      <ArrowRight className={`w-4 h-4 transition-transform duration-300 ${activeAccordion === 'scaling' ? 'rotate-90' : ''}`} />
                    </div>
                  </div>
                </div>
                
                {activeAccordion === 'scaling' && (
                  <div className="mt-8 pt-8 border-t border-gray-400 animate-fade-in">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                        <h4 className="font-bold text-lg mb-4 text-orange-600">What's Included:</h4>
                        <ul className="space-y-3">
                          <li className="flex items-start">
                            <Check className="w-4 h-4 text-orange-600 mr-3 flex-shrink-0 mt-1" />
                            <span className="text-gray-800"><strong>30,000 targeted emails</strong> monthly outbound</span>
                          </li>
                          <li className="flex items-start">
                            <Check className="w-4 h-4 text-orange-600 mr-3 flex-shrink-0 mt-1" />
                            <span className="text-gray-800"><strong>Fully managed system</strong> including inbox management</span>
                          </li>
                          <li className="flex items-start">
                            <Check className="w-4 h-4 text-orange-600 mr-3 flex-shrink-0 mt-1" />
                            <span className="text-gray-800">Dedicated <strong>CSM</strong> and <strong>inbox manager</strong></span>
                          </li>
                          <li className="flex items-start">
                            <Check className="w-4 h-4 text-orange-600 mr-3 flex-shrink-0 mt-1" />
                            <span className="text-gray-800"><strong>Meetings booked</strong> directly on your calendar</span>
                          </li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-bold text-lg mb-4 text-orange-600">Perfect For:</h4>
                        <ul className="space-y-3">
                          <li className="flex items-start">
                            <Check className="w-4 h-4 text-orange-600 mr-3 flex-shrink-0 mt-1" />
                            <span className="text-gray-800">All leads <strong>enriched & personalized</strong></span>
                          </li>
                          <li className="flex items-start">
                            <Check className="w-4 h-4 text-orange-600 mr-3 flex-shrink-0 mt-1" />
                            <span className="text-gray-800">Perfect for <strong>established Product-Market Fit</strong></span>
                          </li>
                          <li className="flex items-start">
                            <Check className="w-4 h-4 text-orange-600 mr-3 flex-shrink-0 mt-1" />
                            <span className="text-gray-800">Teams that know their messaging and ICP</span>
                          </li>
                        </ul>
                        <a 
                          href="#contact" 
                          className="inline-block bg-orange-600 text-white px-6 py-3 rounded-full font-bold hover:bg-orange-500 transition-colors text-center mt-6"
                        >
                          Scale Your Revenue
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Growth Partner Accordion */}
            <div 
              className="bg-[#e8e4d8] rounded-lg relative overflow-hidden cursor-pointer transition-all duration-300"
              style={{
                boxShadow: '4px 4px 0 0 #000',
                border: '3px solid #000',
                transform: 'translate(-4px, -4px)',
              }}
              onClick={() => setActiveAccordion(activeAccordion === 'growth' ? '' : 'growth')}
            >
              <div className="p-6 sm:p-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-center flex-1">
                    <Building className="w-8 h-8 text-orange-600 mr-4 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="text-xl sm:text-2xl font-bold text-black">Growth Partner</h3>
                      <p className="text-gray-700">Enterprise Revenue Partner</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end justify-center ml-4">
                    <div className="text-2xl sm:text-3xl font-bold text-orange-600 mb-1">
                      Custom<span className="text-lg font-normal text-gray-600"> Pricing</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <span className="text-sm mr-2">Details</span>
                      <ArrowRight className={`w-4 h-4 transition-transform duration-300 ${activeAccordion === 'growth' ? 'rotate-90' : ''}`} />
                    </div>
                  </div>
                </div>
                
                {activeAccordion === 'growth' && (
                  <div className="mt-8 pt-8 border-t border-gray-400 animate-fade-in">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                        <h4 className="font-bold text-lg mb-4 text-orange-600">Investment Structure:</h4>
                        <ul className="space-y-3">
                          <li className="flex items-start">
                            <Check className="w-4 h-4 text-orange-600 mr-3 flex-shrink-0 mt-1" />
                            <span className="text-gray-800">Setup Fee: <strong>$15,000</strong></span>
                          </li>
                          <li className="flex items-start">
                            <Check className="w-4 h-4 text-orange-600 mr-3 flex-shrink-0 mt-1" />
                            <span className="text-gray-800">Monthly Retainer: <strong>$7,500</strong></span>
                          </li>
                          <li className="flex items-start">
                            <Check className="w-4 h-4 text-orange-600 mr-3 flex-shrink-0 mt-1" />
                            <span className="text-gray-800">Revenue Share: <strong>10-15%</strong></span>
                          </li>
                          <li className="flex items-start">
                            <Check className="w-4 h-4 text-orange-600 mr-3 flex-shrink-0 mt-1" />
                            <span className="text-gray-800"><strong>12-month minimum</strong> commitment</span>
                          </li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-bold text-lg mb-4 text-orange-600">Perfect For:</h4>
                        <ul className="space-y-3">
                          <li className="flex items-start">
                            <Check className="w-4 h-4 text-orange-600 mr-3 flex-shrink-0 mt-1" />
                            <span className="text-gray-800">Add <strong>5-10% to bottom line</strong> annually</span>
                          </li>
                          <li className="flex items-start">
                            <Check className="w-4 h-4 text-orange-600 mr-3 flex-shrink-0 mt-1" />
                            <span className="text-gray-800">Businesses with <strong>$20M-$50M ARR</strong></span>
                          </li>
                          <li className="flex items-start">
                            <Check className="w-4 h-4 text-orange-600 mr-3 flex-shrink-0 mt-1" />
                            <span className="text-gray-800">We act like <strong>internal revenue team</strong></span>
                          </li>
                          <li className="flex items-start">
                            <Check className="w-4 h-4 text-orange-600 mr-3 flex-shrink-0 mt-1" />
                            <span className="text-gray-800">We do <strong>whatever it takes</strong> to grow</span>
                          </li>
                        </ul>
                        <a 
                          href="#contact" 
                          className="inline-block bg-orange-600 text-white px-6 py-3 rounded-full font-bold hover:bg-orange-500 transition-colors text-center mt-6"
                        >
                          Partner With Us
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="text-center bg-[#e8e4d8] p-8 rounded-lg max-w-4xl mx-auto" style={{
            boxShadow: '4px 4px 0 0 #000',
            border: '3px solid #000',
            transform: 'translate(-4px, -4px)',
          }}>
            <h3 className="text-2xl font-bold text-black mb-4">Not Sure Which Plan Fits?</h3>
            <p className="text-lg text-gray-700 mb-6 max-w-2xl mx-auto">
              Every business is unique. Let's talk about your specific goals, challenges, and growth stage to find the perfect outbound solution for you.
            </p>
            <a 
              href="#contact" 
              className="inline-flex items-center bg-orange-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-orange-500 transition-colors shadow-lg"
            >
              Schedule a Strategy Call <ArrowRight className="ml-2" />
            </a>
          </div>
        </section>

        {/* Why Email section */}
        <section id="why-email" className="mb-12 sm:mb-20 bg-orange-600 text-white p-8 rounded-lg relative overflow-hidden double-border" style={{
          boxShadow: '4px 4px 0 0 #000',
          border: '3px solid #000',
          transform: 'translate(-4px, -4px)',
        }}>
          <h2 className="text-3xl font-bold mb-6 text-center">Believe in Cold Email Again</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold mb-4">Cold Email: The Misunderstood Giant</h3>
              <p className="mb-4">Many have lost faith in cold email, but it remains one of the most powerful tools for B2B growth. When done right, it's not just effective—it's transformative.</p>
              <p>Our approach reignites the potential of cold email, turning skeptics into believers and leads into long-term partnerships.</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-4">Why Cold Email Still Reigns Supreme</h3>
              <ul className="list-disc pl-5 space-y-2">
                <li>Direct access to decision-makers</li>
                <li>Highly scalable outreach</li>
                <li>Precise targeting capabilities</li>
                <li>Measurable ROI and performance</li>
                <li>Ability to start meaningful conversations</li>
              </ul>
            </div>
          </div>
          <div className="mt-8 text-center">
            <a 
              href="#contact" 
              className="inline-flex items-center bg-white text-orange-600 px-6 py-3 rounded-full text-lg font-semibold hover:bg-orange-100 transition-colors"
            >
              Explore Partnership <ArrowRight className="ml-2" />
            </a>
          </div>
        </section>

        <section id="how-it-works" className="mb-12 sm:mb-20">
          <h2 className="text-3xl font-bold mb-8 text-center">How It Works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div
              className="bg-[#e8e4d8] p-6 rounded-lg relative overflow-hidden double-border cursor-pointer"
              style={{
                boxShadow: '4px 4px 0 0 #000',
                border: '3px solid #000',
                transform: 'translate(-4px, -4px)',
              }}
            >
              <div className="flex items-center mb-4">
                <Calendar className="w-6 h-6 text-orange-600 mr-2" />
                <h3 className="text-xl font-semibold">Week 1: Setup & Launch</h3>
              </div>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0 mt-1" />
                  <span>Domain and inbox setup</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0 mt-1" />
                  <span>ICP and target list building</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0 mt-1" />
                  <span>Custom email sequence creation</span>
                </li>
              </ul>
            </div>

            <div
              className="bg-[#e8e4d8] p-6 rounded-lg relative overflow-hidden double-border cursor-pointer"
              style={{
                boxShadow: '4px 4px 0 0 #000',
                border: '3px solid #000',
                transform: 'translate(-4px, -4px)',
              }}
            >
              <div className="flex items-center mb-4">
                <Zap className="w-6 h-6 text-orange-600 mr-2" />
                <h3 className="text-xl font-semibold">Week 2-4: Campaign Execution</h3>
              </div>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0 mt-1" />
                  <span>10,000 targeted emails monthly</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0 mt-1" />
                  <span>Multi-touch follow-up sequences</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0 mt-1" />
                  <span>Weekly performance reporting</span>
                </li>
              </ul>
            </div>

            <div
              className="bg-[#e8e4d8] p-6 rounded-lg relative overflow-hidden double-border cursor-pointer"
              style={{
                boxShadow: '4px 4px 0 0 #000',
                border: '3px solid #000',
                transform: 'translate(-4px, -4px)',
              }}
            >
              <div className="flex items-center mb-4">
                <TrendingUp className="w-6 h-6 text-orange-600 mr-2" />
                <h3 className="text-xl font-semibold">Ongoing: Optimization</h3>
              </div>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0 mt-1" />
                  <span>Reply handling training</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0 mt-1" />
                  <span>Bi-weekly performance tweaks</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0 mt-1" />
                  <span>Continuous list expansion</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* ROI Calculator Section */}
        <section id="roi-calculator" className="mb-12 sm:mb-20">
          <h2 className="text-3xl font-bold mb-8 text-center">Calculate Your ROI</h2>
          
          <div className="bg-[#e8e4d8] p-8 rounded-lg relative overflow-hidden double-border" style={{
            boxShadow: '4px 4px 0 0 #000',
            border: '3px solid #000',
            transform: 'translate(-4px, -4px)',
          }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div className="space-y-6">
                <div>
                  <label className="block text-lg font-semibold mb-2">
                    What's your close rate on sales calls? (%)
                  </label>
                  <input
                    type="number"
                    value={closeRate}
                    onChange={(e) => setCloseRate(Math.max(0, Math.min(100, Number(e.target.value))))}
                    className="w-full px-4 py-2 rounded-lg border-2 border-black focus:outline-none focus:border-orange-600"
                    placeholder="Enter your close rate"
                  />
                </div>
                <div>
                  <label className="block text-lg font-semibold mb-2">
                    What's your average annual customer value? ($)
                  </label>
                  <input
                    type="number"
                    value={annualCustomerValue}
                    onChange={(e) => setAnnualCustomerValue(Math.max(0, Number(e.target.value)))}
                    className="w-full px-4 py-2 rounded-lg border-2 border-black focus:outline-none focus:border-orange-600"
                    placeholder="Enter your annual customer value"
                  />
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg border-2 border-black">
                <h3 className="text-xl font-bold mb-4 text-orange-600">Monthly Projections</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Investment:</span>
                    <span className="text-lg">${roiCalculations.monthlyCost}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Emails Sent:</span>
                    <span className="text-lg">{roiCalculations.monthlyEmails.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Total Replies (3%):</span>
                    <span className="text-lg">{roiCalculations.monthlyReplies}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Positive Replies (15%):</span>
                    <span className="text-lg">{roiCalculations.monthlyPositiveReplies}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Meetings Booked (50%):</span>
                    <span className="text-lg">{roiCalculations.monthlyMeetings}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Estimated Deals:</span>
                    <span className="text-lg">{roiCalculations.monthlyDeals}</span>
                  </div>
                  <div className="flex justify-between items-center text-xl font-bold text-orange-600">
                    <span>Projected Revenue:</span>
                    <span>${Number(roiCalculations.monthlyRevenue).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center text-xl font-bold text-green-600 mt-4 pt-4 border-t-2 border-black">
                    <span>Return on Investment:</span>
                    <span>{roiCalculations.monthlyROI}x</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center">
              <a 
                href="#contact" 
                className="inline-flex items-center bg-orange-600 text-white px-6 py-3 rounded-full text-lg font-semibold hover:bg-orange-500 transition-colors"
              >
                Schedule a Call to Learn More <ArrowRight className="ml-2" />
              </a>
            </div>
          </div>
        </section>

        <section id="contact" className="text-center mb-2">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-orange-600">Schedule a Meeting</h2>
          <p className="text-lg sm:text-xl mb-8 text-gray-700">
            Choose a time that works best for you to discuss how we can help grow your business.
          </p>
          
          <div className="max-w-4xl mx-auto">
            <Cal 
              namespace="email-protocol"
              calLink="sayedb/email-protocol"
              style={{
                width: "100%",
                height: "100%",
                overflow: "scroll"
              }}
              config={{
                layout: "month_view"
              }}
            />
          </div>
        </section>
      </main>

      <footer className="bg-[#e8e4d8] py-6 text-center mt-2 sm:mt-4">
        <div className="flex items-center justify-center gap-4 mb-4">
          <img 
            src="/Whiteboard e (1).png" 
            alt="Email Protocol Logo" 
            className="w-10 h-10 object-contain"
          />
          <p className="text-sm sm:text-base text-gray-700">&copy; 2024 The Email Protocol. All rights reserved.</p>
        </div>
        <p className="text-sm sm:text-base text-orange-600">emailprotocol.net</p>
      </footer>

      {/* Video Modal */}
      {isVideoModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl">
            <div className="relative pt-[56.25%]">
              {/* Wistia Iframe Embed */}
              <iframe
                src="https://fast.wistia.net/embed/iframe/o3ui9a6j36?seo=false&videoFoam=true"
                allow="autoplay; fullscreen"
                allowtransparency="true"
                frameBorder="0"
                scrolling="no"
                title="Wistia Video"
                className="absolute top-0 left-0 w-full h-full rounded-t-lg"
                name="wistia_embed"
              ></iframe>
            </div>
            <div className="p-4 bg-gray-50 rounded-b-lg">
              <button 
                onClick={() => setIsVideoModalOpen(false)}
                className="w-full bg-orange-600 text-white py-2 rounded-full hover:bg-orange-500 transition-colors font-semibold"
              >
                Close Video
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<LandingPage />} />
      </Routes>
    </Router>
  );
}
